/**
 * LiteSabre Pro Scanner Service
 */

import api from './api';

// Web DAST Scan
export const startWebDASTScan = async (data) => {
  const response = await api.post('/litesabre-pro/scan/web-dast', data);
  return response.data;
};

// Mobile App Scan
export const startMobileScan = async (formData) => {
  const response = await api.post('/litesabre-pro/scan/mobile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Desktop App Scan
export const startDesktopScan = async (formData) => {
  const response = await api.post('/litesabre-pro/scan/desktop', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Scan Management
export const getProScans = async (params = {}) => {
  const response = await api.get('/litesabre-pro/scans', { params });
  return response.data;
};

export const getProScan = async (scanId) => {
  const response = await api.get(`/litesabre-pro/scans/${scanId}`);
  return response.data;
};

export const deleteProScan = async (scanId) => {
  const response = await api.delete(`/litesabre-pro/scans/${scanId}`);
  return response.data;
};

// Parse OpenAPI / Swagger / Postman spec upload
export const parseAPISpec = async (file, baseUrl = '') => {
  const formData = new FormData();
  formData.append('file', file);
  if (baseUrl) formData.append('base_url', baseUrl);
  const response = await api.post('/litesabre-pro/parse-api-spec', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Download Executive VAPT Report (Redox/Cobalt-style PDF)
export const downloadExecutiveVAPTReport = async (scanId, scanName = 'scan') => {
  const response = await api.get(
    `/litesabre-pro/scans/${scanId}/executive-report`,
    { responseType: 'blob' }
  );
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = (scanName || 'scan').replace(/\s+/g, '_').slice(0, 40);
  a.download = `executive_vapt_${safe}_${scanId.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Threat model (STRIDE) helpers
export const generateThreatModel = async (scanId, { regenerate = false } = {}) => {
  const response = await api.get(
    `/litesabre-pro/scans/${scanId}/threat-model${regenerate ? '?regenerate=true' : ''}`
  );
  return response.data;
};

export const downloadThreatModelPDF = async (scanId, scanName = 'scan') => {
  const response = await api.get(
    `/litesabre-pro/scans/${scanId}/threat-model/pdf`,
    { responseType: 'blob' }
  );
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = (scanName || 'scan').replace(/\s+/g, '_').slice(0, 40);
  a.download = `threat_model_${safe}_${scanId.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Pentest Profiles
export const getPentestProfiles = async () => {
  const response = await api.get('/litesabre-pro/profiles');
  return response.data;
};

export const createPentestProfile = async (data) => {
  const response = await api.post('/litesabre-pro/profiles', data);
  return response.data;
};

// Dashboard
export const getProDashboard = async () => {
  const response = await api.get('/litesabre-pro/dashboard');
  return response.data;
};

// OWASP Checks
export const getOWASPChecks = async () => {
  const response = await api.get('/litesabre-pro/owasp-checks');
  return response.data;
};

// Scanner Stats (NEW)
export const getScannerStats = async () => {
  const response = await api.get('/litesabre-pro/scanner-stats');
  return response.data;
};

// Constants
export const SCAN_TYPES = [
  { value: 'web_dast', label: 'Web App (DAST)', icon: '🌐', description: 'Dynamic Application Security Testing for web applications' },
  { value: 'mobile_static', label: 'Mobile (Static)', icon: '📱', description: 'Static analysis for Android APK and iOS IPA files' },
  { value: 'mobile_dynamic', label: 'Mobile (Dynamic)', icon: '📲', description: 'Dynamic analysis with network interception' },
  { value: 'desktop_static', label: 'Desktop (Static)', icon: '🖥️', description: 'Static analysis for Windows, macOS, and Linux binaries' },
  { value: 'desktop_network', label: 'Desktop (Network)', icon: '📡', description: 'Network behavior analysis for desktop applications' }
];

export const SCAN_PROFILES = [
  { value: 'quick', label: 'Quick Scan', description: 'Fast baseline scan (15 min)' },
  { value: 'standard', label: 'Standard', description: 'OWASP Top 10 assessment (60 min)' },
  { value: 'comprehensive', label: 'Comprehensive', description: 'Full automated pentest (180 min)' },
  { value: 'api_focused', label: 'API Focused', description: 'API security testing' },
  { value: 'auth_focused', label: 'Auth Focused', description: 'Authentication testing' }
];

export const OWASP_TOP_10 = [
  { id: 'A01:2021', name: 'Broken Access Control', color: 'bg-red-500', description: 'Restrictions on authenticated users are not properly enforced' },
  { id: 'A02:2021', name: 'Cryptographic Failures', color: 'bg-orange-500', description: 'Failures related to cryptography leading to exposure of sensitive data' },
  { id: 'A03:2021', name: 'Injection', color: 'bg-red-600', description: 'SQL, NoSQL, OS, LDAP injection when untrusted data is sent to an interpreter' },
  { id: 'A04:2021', name: 'Insecure Design', color: 'bg-yellow-500', description: 'Missing or ineffective security controls in application design' },
  { id: 'A05:2021', name: 'Security Misconfiguration', color: 'bg-amber-500', description: 'Missing security hardening or improperly configured permissions' },
  { id: 'A06:2021', name: 'Vulnerable Components', color: 'bg-orange-400', description: 'Using components with known vulnerabilities' },
  { id: 'A07:2021', name: 'Auth Failures', color: 'bg-rose-500', description: 'Authentication and session management implemented incorrectly' },
  { id: 'A08:2021', name: 'Integrity Failures', color: 'bg-pink-500', description: 'Code and infrastructure lacking integrity verification' },
  { id: 'A09:2021', name: 'Logging Failures', color: 'bg-blue-400', description: 'Insufficient logging, detection, monitoring, and active response' },
  { id: 'A10:2021', name: 'SSRF', color: 'bg-purple-500', description: 'Server-Side Request Forgery when fetching remote resources without validation' }
];

export const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-100', text: 'text-red-800', badge: 'bg-red-600' },
  high: { bg: 'bg-orange-100', text: 'text-orange-800', badge: 'bg-orange-500' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', badge: 'bg-yellow-500' },
  low: { bg: 'bg-blue-100', text: 'text-blue-800', badge: 'bg-blue-500' },
  info: { bg: 'bg-gray-100', text: 'text-gray-800', badge: 'bg-gray-400' }
};

export const MOBILE_PLATFORMS = [
  { value: 'android', label: 'Android', icon: '🤖', extensions: ['.apk'] },
  { value: 'ios', label: 'iOS', icon: '🍎', extensions: ['.ipa'] }
];

export const DESKTOP_PLATFORMS = [
  { value: 'windows', label: 'Windows', icon: '🪟', extensions: ['.exe', '.dll', '.msi'] },
  { value: 'macos', label: 'macOS', icon: '🍎', extensions: ['.app', '.dmg', '.pkg'] },
  { value: 'linux', label: 'Linux', icon: '🐧', extensions: ['.so', '.deb', '.rpm', ''] }
];

// ============ Jira Integration Functions ============

// Get Jira integration status
export const getJiraStatus = async () => {
  const response = await api.get('/integrations/jira/status');
  return response.data;
};

// Get Jira projects
export const getJiraProjects = async () => {
  const response = await api.get('/integrations/jira/projects');
  return response.data;
};

// Get issue types for a project
export const getJiraIssueTypes = async (projectKey) => {
  const response = await api.get(`/integrations/jira/projects/${projectKey}/issue-types`);
  return response.data;
};

// Create single Jira ticket from finding
export const createJiraTicketFromFinding = async (findingData) => {
  const response = await api.post('/integrations/jira/create-ticket', findingData);
  return response.data;
};

// Create bulk Jira tickets from multiple findings
export const createBulkJiraTickets = async (data) => {
  const response = await api.post('/integrations/jira/create-tickets-bulk', data);
  return response.data;
};

// Get Jira ticket status
export const getJiraTicketStatus = async (projectKey, ticketNumber) => {
  const response = await api.get(`/integrations/jira/ticket-status/${projectKey}/${ticketNumber}`);
  return response.data;
};

// Sync finding status with Jira ticket
export const syncFindingJiraStatus = async (data) => {
  const response = await api.post('/integrations/jira/sync-finding-status', data);
  return response.data;
};

// Sync all Jira tickets for a scan
export const syncAllJiraTickets = async (scanId) => {
  const response = await api.post(`/integrations/jira/sync-all-tickets/${scanId}`);
  return response.data;
};

// ============ Custom Security Rules ============

// Get all custom rules (includes predefined)
export const getCustomRules = async (params = {}) => {
  const response = await api.get('/litesabre-pro/custom-rules', { params });
  return response.data;
};

// Get a specific custom rule
export const getCustomRule = async (ruleId) => {
  const response = await api.get(`/litesabre-pro/custom-rules/${ruleId}`);
  return response.data;
};

// Create a new custom rule
export const createCustomRule = async (data) => {
  const response = await api.post('/litesabre-pro/custom-rules', data);
  return response.data;
};

// Update a custom rule
export const updateCustomRule = async (ruleId, data) => {
  const response = await api.put(`/litesabre-pro/custom-rules/${ruleId}`, data);
  return response.data;
};

// Delete a custom rule
export const deleteCustomRule = async (ruleId) => {
  const response = await api.delete(`/litesabre-pro/custom-rules/${ruleId}`);
  return response.data;
};

// Toggle rule enabled/disabled
export const toggleCustomRule = async (ruleId) => {
  const response = await api.post(`/litesabre-pro/custom-rules/${ruleId}/toggle`);
  return response.data;
};

// Rule categories and severities
export const RULE_CATEGORIES = [
  { value: 'injection', label: 'Injection', icon: 'Database' },
  { value: 'xss', label: 'Cross-Site Scripting (XSS)', icon: 'Code' },
  { value: 'authentication', label: 'Authentication', icon: 'Lock' },
  { value: 'security_headers', label: 'Security Headers', icon: 'Shield' },
  { value: 'information_disclosure', label: 'Information Disclosure', icon: 'Eye' },
  { value: 'misconfiguration', label: 'Misconfiguration', icon: 'Settings' },
  { value: 'cryptography', label: 'Cryptography', icon: 'Key' },
  { value: 'custom', label: 'Custom', icon: 'Wrench' }
];

export const RULE_SEVERITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
  { value: 'low', label: 'Low', color: 'bg-blue-500' },
  { value: 'info', label: 'Info', color: 'bg-gray-500' }
];

export const OWASP_MAPPINGS = [
  { value: 'A01:2021', label: 'A01:2021 - Broken Access Control' },
  { value: 'A02:2021', label: 'A02:2021 - Cryptographic Failures' },
  { value: 'A03:2021', label: 'A03:2021 - Injection' },
  { value: 'A04:2021', label: 'A04:2021 - Insecure Design' },
  { value: 'A05:2021', label: 'A05:2021 - Security Misconfiguration' },
  { value: 'A06:2021', label: 'A06:2021 - Vulnerable Components' },
  { value: 'A07:2021', label: 'A07:2021 - Auth Failures' },
  { value: 'A08:2021', label: 'A08:2021 - Software & Data Integrity' },
  { value: 'A09:2021', label: 'A09:2021 - Logging Failures' },
  { value: 'A10:2021', label: 'A10:2021 - SSRF' }
];

export const TEST_LOCATIONS = [
  { value: 'query_params', label: 'Query Parameters' },
  { value: 'headers', label: 'HTTP Headers' },
  { value: 'body', label: 'Request Body' },
  { value: 'path', label: 'URL Path' },
  { value: 'cookies', label: 'Cookies' }
];

// ============ Rule Import/Export ============

// Export rules as JSON or YAML
export const exportCustomRules = async (format = 'json', includePredefined = false) => {
  const response = await api.get('/litesabre-pro/custom-rules/export', {
    params: { format, include_predefined: includePredefined },
    responseType: 'blob'
  });
  return response.data;
};

// Import rules from file
export const importCustomRules = async (file, overwriteExisting = false) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/litesabre-pro/custom-rules/import?overwrite_existing=${overwriteExisting}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Get rule templates
export const getRuleTemplates = async () => {
  const response = await api.get('/litesabre-pro/custom-rules/templates');
  return response.data;
};

// ============ AI Pentest Agent ============

export const startAIPentest = async (data) => {
  const response = await api.post('/litesabre-pro/pentest/start', data);
  return response.data;
};

export const getPentestSessions = async (params = {}) => {
  const response = await api.get('/litesabre-pro/pentest/sessions', { params });
  return response.data;
};

export const getPentestSession = async (sessionId) => {
  const response = await api.get(`/litesabre-pro/pentest/sessions/${sessionId}`);
  return response.data;
};

export const getPentestExecutionLog = async (sessionId) => {
  const response = await api.get(`/litesabre-pro/pentest/sessions/${sessionId}/log`);
  return response.data;
};

export const deletePentestSession = async (sessionId) => {
  const response = await api.delete(`/litesabre-pro/pentest/sessions/${sessionId}`);
  return response.data;
};

export const rescanPentestSession = async (sessionId) => {
  const response = await api.post(`/litesabre-pro/pentest/sessions/${sessionId}/rescan`);
  return response.data;
};

export const getPentestTools = async () => {
  const response = await api.get('/litesabre-pro/pentest/tools');
  return response.data;
};

export const exportPentestPdf = async (sessionId) => {
  const response = await api.get(`/litesabre-pro/pentest/sessions/${sessionId}/export-pdf`, {
    responseType: 'blob',
  });
  return response.data;
};

export const scrapeBugBountyScope = async (url) => {
  const response = await api.post('/litesabre-pro/pentest/scrape-scope', { url });
  return response.data;
};

export const importBurpConfig = async (configJson) => {
  const response = await api.post('/litesabre-pro/pentest/import-burp-config', { config_json: configJson });
  return response.data;
};

export const scrapeGuidelines = async (url) => {
  const response = await api.post('/litesabre-pro/pentest/scrape-guidelines', { url });
  return response.data;
};

export const getVulnerabilityKB = async () => {
  const response = await api.get('/litesabre-pro/pentest/vulnerability-kb');
  return response.data;
};

// ============ AI Configuration ============

export const getAIProviders = async () => {
  const response = await api.get('/litesabre-pro/ai-config/providers');
  return response.data;
};

export const getAIConfig = async () => {
  const response = await api.get('/litesabre-pro/ai-config');
  return response.data;
};

export const updateAIConfig = async (data) => {
  const response = await api.put('/litesabre-pro/ai-config', data);
  return response.data;
};

// ============ LiteSabre Confidence Scores ============

export const calculateConfidenceScores = async (scanId) => {
  const response = await api.post(`/litesabre/scans/${scanId}/calculate-confidence`);
  return response.data;
};

export const getConfidenceSummary = async (scanId) => {
  const response = await api.get(`/litesabre/scans/${scanId}/confidence-summary`);
  return response.data;
};

